import { omit, capitalize, mapValues, isObject, get, set, chain, isArray } from "lodash"

/**
 * Gets the latest (last) item from an array, optionally extracting a specific path
 */
export function getLatestData(dataArray, path = null) {
  if (!dataArray || dataArray.length === 0) return null
  const latest = dataArray[dataArray.length - 1]
  return path ? get(latest, path) : latest
}

/**
 * Creates keyed contributions from an array of data items
 */
export function createKeyedContributions(dataArray, keyPath, valuePath) {
  return dataArray.map((item) => {
    return chain(item)
      .get(keyPath, [])
      .keyBy("id")
      .mapValues(data => ({
        id: data.id,
        value: get(data, valuePath),
      }))
      .value()
  })
}

/**
 * Generic recursive function to map factor values to groups
 * Handles both aggregation and non-aggregation based on provided keys
 */
export function recursiveMapFactorValues(
  factorValues,
  groupedFactors,
  factorCategory,
  nonAggregableKeys = {},
  aggregationFn = null,
  key = null,
  parentKey = null,
) {
  // Don't aggregate certain values at the category level
  if (nonAggregableKeys[key]) {
    return null
  }

  if (isArray(factorValues)) {
    return factorValues.map(({ value }, index) => {
      return recursiveMapFactorValues(
        value,
        groupedFactors,
        factorCategory,
        nonAggregableKeys,
        aggregationFn,
        index,
        key,
      )
    })
  }

  if (isObject(factorValues)) {
    return mapValues(factorValues, (value, factorKey) =>
      recursiveMapFactorValues(
        value,
        groupedFactors,
        factorCategory,
        nonAggregableKeys,
        aggregationFn,
        factorKey,
        key,
      ),
    )
  }

  // At the final level of recursion, factorValues is a single value
  const newValue = factorValues || 0

  if (parentKey) {
    const groupedFactorValue = get(groupedFactors, [factorCategory, "values", parentKey, key]) || 0

    // Use custom aggregation function if provided, otherwise default to addition
    if (aggregationFn) {
      return aggregationFn(groupedFactorValue, newValue, parentKey, key)
    }

    return groupedFactorValue + newValue
  }

  return newValue
}

/**
 * Updates grouped factor values and running totals
 * Used specifically for performance calculations that need both grouping and totaling
 */
export function updateGroupedAndTotalValues(value, factorsTotal, groupedFactors, factorCategory, key, parentKey) {
  const groupedFactorValue = get(groupedFactors, [factorCategory, "values", parentKey, key]) || 0
  const currentTotal = get(factorsTotal, [parentKey, key], 0)

  set(factorsTotal, [parentKey, key], currentTotal + value)
  return groupedFactorValue + value
}

/**
 * Generic function to group factors by category
 * Can be used with or without totals tracking
 */
export function groupFactorsByCategory(
  factors,
  nonAggregableKeys = {},
  includeFactorsTotal = false,
  customMappingFn = null,
) {
  const initialAcc = includeFactorsTotal
    ? { groupedFactors: {}, factorsTotal: {} }
    : {}

  return Object.values(factors).reduce((acc, factor) => {
    const groupedFactors = includeFactorsTotal ? acc.groupedFactors : acc
    const factorsTotal = includeFactorsTotal ? acc.factorsTotal : undefined

    let values
    if (customMappingFn) {
      values = customMappingFn({ values: factor.values, factorsTotal, groupedFactors, factorCategory: factor.category })
    } else {
      values = recursiveMapFactorValues(
        factor.values,
        groupedFactors,
        factor.category,
        nonAggregableKeys,
      )
    }

    if (!groupedFactors[factor.category]) {
      groupedFactors[factor.category] = {
        label: capitalize(factor.category),
        values,
        children: [omit(factor, "category")],
      }
    } else {
      groupedFactors[factor.category] = {
        ...groupedFactors[factor.category],
        children: [...groupedFactors[factor.category].children, omit(factor, "category")],
        values,
      }
    }

    return includeFactorsTotal ? { groupedFactors, factorsTotal } : groupedFactors
  }, initialAcc)
}

/**
 * Common non-aggregable keys for risk calculations
 */
export const RISK_NON_AGGREGABLE_KEYS = {
  valueAtRisk: true,
  predictedRisk: true,
  netExposure: true,
}

/**
 * Common non-aggregable keys for factor sensitivities calculations
 */
export const FACTOR_SENSITIVITY_NON_AGGREGABLE_KEYS = {
  predicted: true,
  historical: true,
}

/**
 * Common non-aggregable keys for performance calculations
 */
export const PERFORMANCE_NON_AGGREGABLE_KEYS = {
  annualizedReturns: true,
  annualizedVolatility: true,
  informationRatio: true,
  percentPriceChange1Day: true,
  percentPriceChangeCumulative: true,
  normalizedReturn: true,
  netExposure: true,
}

/**
 * Helper to create factor metadata subquery
 */
export const FACTOR_METADATA_SUBQUERY = `
  id
  name
  category
`

export function filterOptionsByCategory(factors, category) {
  if (!factors) {
    return []
  }
  if (!category) {
    return factors
  }
  return factors.filter(f => f.category?.toLowerCase() === category.toLowerCase())
}

export function getFactorValueByMetricDisplay(factor, metricDisplay) {
  if (!factor || !metricDisplay) {
    return factor || 0
  }
  return factor[metricDisplay] ?? 0
}
